import * as React from 'react';

import { useRequest } from 'ahooks';
import {
  Alert,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import {
  AntdDateRangePicker,
  DecimalInputNumber,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  PrimitiveTreeSelect,
} from 'components';
import { useSpecialOfferAction, useSpecialOfferValue } from 'context';
import { getBrands, getCategories, searchProducts } from 'services';
import {
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  normalizeToString,
  PATHNAME,
  UNIT,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;
const { PERCENT } = UNIT;

type ProductsParams = Parameters<typeof searchProducts>[number];

type FieldType = Pick<
  ReturnType<typeof useSpecialOfferValue>,
  'title' | 'percent' | 'duration' | 'products' | 'brands' | 'categories'
>;

const SpecialOfferFormContent = () => {
  const {
    title,
    percent,
    duration,
    products: initialProducts = [],
    brands: initialBrands = [],
    categories: initialCategories = [],
  } = useSpecialOfferValue();
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });
  const {
    data: productsData,
    error: productsError,
    params: productsParams,
    loading: isProductsLoading,
    run: searchProductsRun,
  } = useRequest(
    async ({ searchKey }: Pick<ProductsParams, 'searchKey'>) =>
      await searchProducts({
        searchKey,
        sortType: DEFAULT_SORT_TYPE,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_MAX_PAGE_SIZE,
      }),
    {
      defaultParams: [{ searchKey: '' }],
      debounceWait: 1000,
    },
  );
  const action = useSpecialOfferAction();

  const { startTime: initialStartTime, endTime: initialEndTime } =
    duration ?? {};
  const { categories = [] } = categoriesData ?? {};
  const { brands = [] } = brandsData ?? {};
  const { total: totalProducts = 0, data: products = [] } = productsData ?? {};
  const { searchKey: currentSearchKey = '' } = productsParams.at(0) ?? {};

  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );
  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const productsSelectOptions = generateSelectOptions(
    products,
    'id',
    'name',
    'id',
  );

  return (
    <Row className='px-6 py-8'>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='عنوان'
          name='title'
          rules={[inputRule.required, inputRule.string]}
          initialValue={title}
        >
          <Input placeholder='عنوان تخفیف ویژه را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='مقدار'
          name='percent'
          rules={[inputRule.required, inputRule.digit]}
          initialValue={percent}
        >
          <DecimalInputNumber
            placeholder='مقدار تخفیف ویژه را وارد کنید'
            addonAfter={PERCENT}
            max={100}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='بازه زمانی'
          name='duration'
          rules={[inputRule.required]}
          initialValue={
            initialStartTime && initialEndTime
              ? [dayjs(initialStartTime), dayjs(initialEndTime)]
              : undefined
          }
        >
          <AntdDateRangePicker
            placeholder={['تاریخ شروع تخفیف ویژه', 'تاریخ پایان تخفیف ویژه']}
            onDateChange={([startTime, endTime]) => {
              if (!startTime || !endTime) return;

              action({
                type: 'UPDATE_SPECIAL_OFFER_DURATION',
                payload: {
                  duration: {
                    startTime,
                    endTime,
                  },
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Divider dashed className='mb-4 mt-0' />
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='برندها'
          name='brands'
          initialValue={initialBrands.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          }))}
        >
          <Select
            allowClear
            labelInValue
            mode='multiple'
            placeholder='برندهای تخفیف ویژه را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='دسته‌بندی‌ها'
          name='categories'
          initialValue={initialCategories.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          }))}
        >
          <PrimitiveTreeSelect
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های تخفیف ویژه را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='محصول‌ها'
          name='products'
          initialValue={initialProducts.map(({ id, name }) => ({
            key: id,
            label: name,
            value: id,
          }))}
        >
          <Select
            allowClear
            labelInValue
            filterOption={false}
            popupMatchSelectWidth={false}
            mode='multiple'
            placeholder='محصول‌های تخفیف ویژه را انتخاب کنید'
            options={productsSelectOptions}
            notFoundContent={
              isProductsLoading ? (
                <div className='grid min-h-24 place-content-center gap-4'>
                  <Spin />
                  <Typography.Text className='text-[--ant-color-info]'>
                    در حال بارگذاری محصول‌ها...
                  </Typography.Text>
                </div>
              ) : productsError ? (
                <div className='grid min-h-24 content-center justify-items-center gap-4'>
                  <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                  <Typography.Text type='danger'>
                    {productsError instanceof Error
                      ? productsError.message
                      : String(productsError)}
                  </Typography.Text>
                </div>
              ) : !totalProducts ? (
                <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                  <InboxOutlinedIcon className='text-3xl' />
                  <Typography.Text type='secondary'>
                    <span>هیچ محصولی با عنوان</span>
                    <span> &rdquo;{currentSearchKey}&rdquo; </span>
                    <span>وجود ندارد.</span>
                  </Typography.Text>
                </div>
              ) : undefined
            }
            onSearch={value => {
              const searchKey = value.trim();
              searchProductsRun({ searchKey });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Flex vertical gap='small'>
          <Alert
            showIcon
            type='warning'
            message='تخفیف تعیین‌شده در این بخش، ملاک اصلی بوده و تخفیف‌های قبلی (حتی تخفیف در محصول‌ها) را باطل می‌کند.'
          />
          <Alert
            showIcon
            type='warning'
            message='تخفیف نهایی، بر اشتراک کلیه گزینه‌های انتخاب‌شده (برندها، دسته‌بندی‌ها و محصول‌ها)، محاسبه و اعمال می‌گردد.'
          />
        </Flex>
      </Col>
    </Row>
  );
};

export { SpecialOfferFormContent };
